/* eslint-disable complexity */

/* eslint-disable max-lines-per-function */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { Keys, locale } from 'src/i18n';
import { getState } from 'src/store';
import axios from 'src/workspace/axios';
import { getClientConfigData } from 'src/workspace/utils';
import { getBaseUrl, getWebsocketHost } from './constants';
import { transformMessageCtas } from './queries';
import { getClientId, getRetry, getSocket, getThreadId } from './selector';
import {
  addMessage,
  clearStore,
  setClientId,
  setIsLoading,
  setIsTyping,
  setLastUpdated,
  setMessages,
  setNewSession,
  setReplies,
  setRetryCount,
  setSampleQuestions,
  setShowMenu,
  setShowToast,
  setSocket,
  setStatus,
  setThreadId,
  setUploadedFile,
} from './slice';
import { HistoryPayload, ILead, Message, MESSAGE_TYPE, SENDER } from './types';

export const getHistory = createAsyncThunk(
  'get/history',
  async ({ client_id, thread_id }: HistoryPayload, { dispatch }) => {
    try {
      const client = getClientConfigData()?.client;
      dispatch(setIsLoading(true));
      const queryParams = {
        client_id,
        thread_id,
      };
      const apiUrl = new URL('/chat_history', getBaseUrl(client));
      Object.keys(queryParams).forEach((key) =>
        apiUrl.searchParams.append(key, queryParams[key]),
      );

      const { data: response }: any = await axios.get(apiUrl.toString());
      const history: Array<Message> = response?.chat_history.map(
        (item: {
          leads_data: ILead[];
          tabular_data: [];
          message_type: string;
          sender: string;
          text: string;
        }) => ({
          sender: item?.sender || SENDER.USER,
          text: item?.text || '',
          message_type: item?.message_type || 'TEXT',
          tabular_data: item?.tabular_data || [],
          ctas: transformMessageCtas(item),
        }),
      );
      dispatch(setMessages(history));
      dispatch(setIsLoading(false));
    } catch (error) {
      dispatch(setIsLoading(false));
      dispatch(
        setStatus({
          type: 'info',
          message: 'Failed to fetch history',
          duration: 2000,
        }),
      );
      dispatch(setShowToast(true));
    }
  },
);

export const clearHistory = createAsyncThunk(
  'clear/history',
  async (_, { dispatch }) => {
    const clienId = getClientId(getState());
    const threadId = getThreadId(getState());
    const client = getClientConfigData()?.client;
    try {
      dispatch(setIsLoading(true));
      const queryParams = {
        client_id: clienId,
        thread_id: threadId,
      };
      const apiUrl = new URL('/chat_history', getBaseUrl(client));

      Object.keys(queryParams).forEach((key) =>
        apiUrl.searchParams.append(key, queryParams[key]),
      );
      axios.delete(apiUrl.toString());
      dispatch(setMessages([]));
      dispatch(setShowMenu(false));
      dispatch(setIsLoading(false));
      dispatch(setNewSession(true));
      dispatch(setReplies([]));
    } catch (error) {
      dispatch(setIsLoading(false));
      dispatch(
        setStatus({
          type: 'info',
          message: 'Failed to clear history',
          duration: 2000,
        }),
      );
      dispatch(setShowToast(true));
    }
  },
);

export const disconnectWebSocket = createAsyncThunk(
  'copilot/disconnect',
  async (_, { dispatch }) => {
    const socket = getSocket(getState());
    try {
      socket?.close();
      dispatch(setSocket(null));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Socket disconnection failed.', e);
    }
  },
);

export const sendMessage = createAsyncThunk(
  'copilot/send',
  async (
    {
      text,
      file,
      metaData,
      context,
    }: {
      text: string;
      file?: File | null;
      metaData?: any;
      context?: any;
    },
    { dispatch },
  ) => {
    try {
      const socket = getSocket(getState());
      const newMessage: Message = {
        sender: 'USER',
        message_type: MESSAGE_TYPE.TEXT,
        text,
      };
      const data: any = { question: text, context };
      if (file) {
        // create URL using S3
        dispatch(
          setStatus({
            type: 'info',
            message: 'Uploading File...',
            duration: 5000,
          }),
        );
        const res: any = await axios.get('/api/getS3signedUrl', {
          params: {
            file_name: file.name,
            file_type: file.type,
          },
        });

        const { signed_request: signedRequest, pre_signed_url: preSignedUrl } =
          res;
        await axios({
          url: signedRequest,
          method: 'put',
          data: file,
          headers: {
            'Content-Type': file.type,
          },
          withCredentials: false,
        });

        newMessage.file_data = {
          file: preSignedUrl,
          type: file?.type || '',
        };
        data.file_url = preSignedUrl;
        data.meta_data = metaData;
        dispatch(
          setStatus({
            type: 'success',
            message: 'File uploaded.',
          }),
        );
      }

      dispatch(setLastUpdated(Date.now()));
      dispatch(addMessage(newMessage));
      dispatch(setUploadedFile(null));
      dispatch(setReplies([]));
      dispatch(setIsTyping(true));

      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(data));
      } else if (socket) {
        const timeoutId = setTimeout(() => {
          dispatch(
            setStatus({
              type: 'error',
              message: locale(Keys.FAILED_TO_CONNECT_PLEASE_TRY_AGAIN),
            }),
          );
        }, 10000);
        socket.addEventListener('open', () => {
          clearTimeout(timeoutId);
          socket.send(JSON.stringify(data));
        });
      } else {
        dispatch(
          setStatus({
            type: 'error',
            message: locale(Keys.DISCONNECTED_PLEASE_TRY_AGAIN),
          }),
        );
        dispatch(setIsTyping(false));
      }
    } catch (e: any) {
      dispatch(
        setStatus({
          type: 'error',
          message: `Error while sending message: ${e.message}`,
          duration: 2000,
        }),
      );
      dispatch(setIsTyping(false));
    }
  },
);

export const connectWebSocket = createAsyncThunk(
  'copilot/connect',
  async (_, { dispatch }) => {
    const configData = getClientConfigData();
    const { client } = configData || {};
    const authToken =
      document.cookie.replace(
        /(?:(?:^|.*;\s*)auth-token\s*=\s*([^;]*).*$)|^.*$/,
        '$1',
      ) || configData.auth_token;
    const visitorToken = document.cookie.replace(
      /(?:(?:^|.*;\s*)visitor_token\s*=\s*([^;]*).*$)|^.*$/,
      '$1',
    );
    dispatch(
      setStatus({
        type: 'info',
        message: 'Connecting to copilot...',
      }),
    );

    const retryCount = getRetry(getState());

    const metaDataQuery: any = {
      initiated_from: 'home_screen',
      sources: [],
      extras: {
        lead_code: 'lead_code',
      },
      visitor: Boolean(!authToken && visitorToken),
      auth_token: String(authToken || visitorToken),
    };

    if (metaDataQuery.visitor) {
      metaDataQuery.portal_id = window.portalId;
    }

    const metaDataQueryString = encodeURIComponent(
      JSON.stringify(metaDataQuery),
    );

    const newSocket = new WebSocket(
      `wss://${getWebsocketHost(
        client,
      )}/ws/start_chat?meta_data=${metaDataQueryString}`,
    );

    dispatch(
      setStatus({
        type: 'info',
        message: 'Connecting...',
        duration: 100000,
      }),
    );

    dispatch(setSocket(newSocket));

    newSocket.onopen = () => {
      dispatch(
        setStatus({
          type: 'success',
          message: 'Connected',
        }),
      );
    };

    newSocket.onmessage = (event) => {
      const receivedMessage = JSON.parse(event.data);
      if (receivedMessage.status === 'FAILURE') {
        dispatch(
          setStatus({
            type: 'error',
            message: 'Failed to generate response',
            duration: 2000,
          }),
        );
        dispatch(setShowToast(true));
        dispatch(setIsTyping(false));
        dispatch(setReplies([]));
      } else if (receivedMessage.status === 'THREAD') {
        const {
          thread_id: threadId,
          client_id: clientId,
          sample_questions: sampleQuestions,
        } = receivedMessage;

        dispatch(setSampleQuestions(sampleQuestions || []));
        dispatch(setClientId(clientId));
        dispatch(setThreadId(threadId));
        if (threadId && clientId) {
          dispatch(getHistory({ thread_id: threadId, client_id: clientId }));
        }
      } else {
        const newMessageItem = {
          sender: SENDER.AI,
          text: receivedMessage?.response.message || ' ',
          message_type:
            receivedMessage.response?.message_type || MESSAGE_TYPE.TEXT,
          tabular_data: receivedMessage.response?.tabular_data || [],
          ctas: transformMessageCtas(receivedMessage.response),
        };
        dispatch(setReplies(receivedMessage.response.follow_up_questions));
        dispatch(addMessage(newMessageItem));
      }
      dispatch(setIsTyping(false));
    };

    newSocket.onclose = () => {
      if (retryCount < 5) {
        dispatch(setRetryCount(retryCount + 1));
        dispatch(connectWebSocket());
      } else {
        dispatch(
          setStatus({
            type: 'error',
            message: 'Disconnected',
          }),
        );
        dispatch(setIsLoading(false));
        dispatch(setSocket(null));
        dispatch(setRetryCount(0));
      }
    };
    return () => {
      dispatch(disconnectWebSocket());
    };
  },
);

const onActivate = () => {};

const onDeactivate = ({ dispatch }) => {
  dispatch(clearStore());
  dispatch(disconnectWebSocket());
};

export default { onActivate, onDeactivate };
